/**
 * ResetPassword: set a new password from the ?token= reset link.
 *
 * Also the landing page for staff invites (?invite=1: "choose your
 * password") and staff resets (?staff=1); both send the person on to the
 * staff sign-in afterwards instead of the resident one.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { confirmPasswordReset } from '../lib/auth';
import { AuthField, AuthShell, AuthSubmit } from '../components/AuthShell';

function errorMessage(error: unknown, t: (key: string) => string): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : t('auth.resetFailed');
}

export function ResetPassword(): JSX.Element {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const invite = params.get('invite') === '1';
  const staff = invite || params.get('staff') === '1';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError(t('auth.passwordMismatch'));
      return;
    }
    setBusy(true);
    try {
      await confirmPasswordReset(token, password);
      setDone(true);
    } catch (err) {
      setError(errorMessage(err, t));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell title={invite ? t('auth.inviteTitle') : t('auth.resetTitle')}>
      {!token ? (
        <p role="alert" className="text-sm text-red-600">
          {t('auth.resetMissing')}
        </p>
      ) : done ? (
        <div>
          <p role="status" className="text-sm text-stone-700">
            {invite ? t('auth.inviteDone') : t('auth.resetDone')}
          </p>
          <Link
            to={staff ? '/admin' : '/signin'}
            className="mt-4 inline-block rounded-lg bg-tenant-primary px-4 py-2 text-sm font-medium text-white hover:bg-tenant-strong"
          >
            {staff ? t('auth.staffSignInButton') : t('auth.signInButton')}
          </Link>
        </div>
      ) : (
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <AuthField
            label={t('auth.newPassword')}
            type="password"
            required
            autoComplete="new-password"
            placeholder={t('auth.passwordPlaceholder')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <AuthField
            label={t('auth.confirmPassword')}
            type="password"
            required
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
          {error && (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          )}
          <AuthSubmit busy={busy} busyLabel={t('auth.updating')}>
            {t('auth.updatePassword')}
          </AuthSubmit>
        </form>
      )}
    </AuthShell>
  );
}
